/**
 * The pipeline board. Columns come from `GET /pipeline`, never from a hard-coded list in the UI,
 * and every move asks for a reason before it is written.
 *
 * The vocabulary behind those columns is 017's CHECK constraint — new, engaged, qualified, ready,
 * handed_over, closed — and NOT the pack's: the plan puts `funnel.stages` in the pack schema, but
 * inventing a second vocabulary the database would refuse to store is worse than using the one it
 * enforces (`routes/pipeline.ts` says the same, at more length).
 *
 * Drag-and-drop uses the native HTML5 API rather than a drag library — one less runtime dependency
 * for a board this size. Because native DnD is unusable from a keyboard, every card ALSO carries a
 * "Move to" select. That is not a fallback, it is the accessible path.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import { useState, type DragEvent, type ReactNode } from 'react';
import type { PipelineCard, PipelineStage } from '../lib/types.ts';
import { formatAgo } from '../lib/format.ts';
import { TierPill } from './Pill.tsx';

export interface KanbanProps {
  stages: PipelineStage[];
  cards: PipelineCard[];
  /** Called with the reason the mover typed; the caller writes it to the audit trail. */
  onMove: (leadId: string, toStage: string) => void;
  onOpen: (leadId: string) => void;
}

export function Kanban({ stages, cards, onMove, onOpen }: KanbanProps): ReactNode {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const drop = (stageKey: string) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/h1-lead') || dragging;
    setDragging(null);
    setOver(null);
    if (leadId) onMove(leadId, stageKey);
  };

  return (
    <div className="h1-scroll flex h-full gap-3 overflow-x-auto pb-3" data-testid="kanban">
      {stages.map((stage) => {
        const column = cards.filter((c) => c.stage === stage.key);
        return (
          <div
            key={stage.key}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(stage.key);
            }}
            onDragLeave={() => setOver((s) => (s === stage.key ? null : s))}
            onDrop={drop(stage.key)}
            // Named for the drag journey in `e2e/console/03-pipeline-stage.spec.ts`: a column is
            // a plain div with no role of its own, and a drop target you can only find by its
            // heading text is a test that breaks when somebody renames a stage label.
            data-testid="kanban-column"
            data-stage={stage.key}
            className={clsx(
              'flex w-72 shrink-0 flex-col rounded-[var(--h1-radius)] border bg-bg-subtle',
              over === stage.key ? 'border-brand ring-2 ring-[var(--h1-brand)]' : 'border-line',
            )}
          >
            <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
              <h3 className="text-xs font-semibold tracking-wide text-fg uppercase">{stage.label}</h3>
              <span
                className="rounded-full bg-neutral-soft px-2 py-0.5 text-[11px] font-medium text-neutral-soft-fg"
                title="Counted server-side under the same filters as the cards"
              >
                {stage.count}
              </span>
            </header>
            <div className="h1-scroll flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto p-2">
              {column.length === 0 && <p className="px-1 py-3 text-[11px] text-fg-subtle">No leads in this stage.</p>}
              {column.map((card) => (
                <article
                  key={card.leadId}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/h1-lead', card.leadId);
                    e.dataTransfer.effectAllowed = 'move';
                    setDragging(card.leadId);
                  }}
                  onDragEnd={() => setDragging(null)}
                  className={clsx(
                    'h1-card cursor-grab p-2.5 active:cursor-grabbing',
                    dragging === card.leadId && 'opacity-50',
                  )}
                  data-testid="kanban-card"
                  data-lead={card.leadId}
                  data-stage={card.stage}
                >
                  <button
                    type="button"
                    onClick={() => onOpen(card.leadId)}
                    className="block w-full text-left text-sm font-medium text-fg hover:text-brand-ink"
                  >
                    {card.displayName}
                  </button>
                  <p className="mt-0.5 line-clamp-2 text-[11px] text-fg-muted">{card.headline}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <TierPill tier={card.tier} score={card.score} />
                    {/* No deal value on a card: `routes/pipeline.ts` sends `valueInr: null` for
                        every lead, because nothing in this product collects one. */}
                    <span className="ml-auto text-[10px] text-fg-subtle">{formatAgo(card.lastInboundAt)}</span>
                  </div>
                  <label className="mt-2 flex items-center gap-1.5 text-[10px] text-fg-subtle">
                    <span className="sr-only sm:not-sr-only">Move to</span>
                    <select
                      value={card.stage}
                      onChange={(e) => onMove(card.leadId, e.target.value)}
                      className="flex-1 rounded border border-line-strong bg-surface px-1 py-0.5 text-[11px] text-fg"
                      aria-label={`Move ${card.displayName} to another stage`}
                    >
                      {stages.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </article>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
